import { useState, useEffect } from 'react';
import { Eye } from 'lucide-react';

interface ListLoadingIndicatorProps {
  isLoadingNostr?: boolean;
  isLoadingScryfall?: boolean;
  isImporting?: boolean;
  variant?: 'overlay' | 'inline' | 'import';
  title?: string;
}

export function ListLoadingIndicator({
  isLoadingNostr = false,
  isLoadingScryfall = false,
  isImporting = false,
  variant = 'overlay',
  title = "Take your time."
}: ListLoadingIndicatorProps) {
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    setIsVisible(isLoadingNostr || isLoadingScryfall || isImporting);
  }, [isLoadingNostr, isLoadingScryfall, isImporting]);

  if (!isVisible) return null;

  const containerClasses = {
    overlay: "fixed inset-0 bg-background/40 backdrop-blur-sm z-50 flex items-center justify-center",
    inline: "w-full h-96 flex items-center justify-center bg-transparent",
    import: "w-full h-full flex items-center justify-center bg-background/50 backdrop-blur-sm"
  };

  const cardSizeClasses = {
    overlay: "w-200 h-280",
    inline: "w-160 h-224",
    import: "w-120 h-168"
  };

  const eyeSizeClasses = {
    overlay: "w-60 h-60",
    inline: "w-48 h-48",
    import: "w-36 h-36"
  };

  return (
    <div className={containerClasses[variant]}>
      <div className="flex flex-col items-center space-y-6">
        {/* Surveil Card Animation - Based on nostrdamus tarot card effect */}
        <div className="relative">
          <div className="surveil-card">
            <div className="surveil-card-inner">
              {/* Card Back */}
              <div className="surveil-card-back">
                <div className="card-back-design">
                  <div className="surveil-eye-container">
                    <Eye className="surveil-eye" />
                  </div>
                  <div className="surveil-glow" />
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Loading Text */}
        <div className="text-center space-y-3">
          <h3 className="text-xl font-bold text-foreground">{title}</h3>

          {/* Loading Indicators */}
          <div className="space-y-2">
            {isLoadingNostr && (
              <div className="flex items-center space-x-2">
                <div className="w-2 h-2 bg-blue-500 rounded-full animate-pulse" />
                <span className="text-sm text-muted-foreground">Loading deck from Nostr</span>
              </div>
            )}

            {isLoadingScryfall && (
              <div className="flex items-center space-x-2">
                <div className="w-2 h-2 bg-green-500 rounded-full animate-pulse" />
                <span className="text-sm text-muted-foreground">Fetching card data from Scryfall</span>
              </div>
            )}

            {isImporting && (
              <div className="flex items-center space-x-2">
                <div className="w-2 h-2 bg-purple-500 rounded-full animate-pulse" />
                <span className="text-sm text-muted-foreground">Importing deck list using Scryfall</span>
              </div>
            )}
          </div>
        </div>

        <style>{`
          .surveil-card {
            perspective: 1500px;
            width: ${cardSizeClasses[variant].split(' ')[0].replace('w-', '')}px;
            height: ${cardSizeClasses[variant].split(' ')[1].replace('h-', '')}px;
            cursor: pointer;
          }

          .surveil-card-inner {
            position: relative;
            width: 100%;
            height: 100%;
            transform-style: preserve-3d;
            box-shadow: 0 10px 25px rgba(0, 0, 0, 0.2), 0 6px 10px rgba(0, 0, 0, 0.22);
            border-radius: 15px;
            animation: surveil-tilt-rotate 4s ease-in-out infinite;
          }

          .surveil-card-back {
            position: absolute;
            width: 100%;
            height: 100%;
            background: linear-gradient(45deg, #0f172a, #1e293b);
            border: 2px solid hsl(210 100% 56% / 0.3);
            border-radius: 15px;
            display: flex;
            align-items: center;
            justify-content: center;
            overflow: hidden;
          }

          .card-back-design {
            width: 100%;
            height: 100%;
            position: relative;
            overflow: hidden;
            border-radius: 12px;
            background: linear-gradient(135deg,
              hsl(210 100% 56% / 0.1) 0%,
              hsl(210 100% 56% / 0.05) 50%,
              hsl(210 100% 56% / 0.1) 100%);
          }

          .surveil-eye-container {
            position: absolute;
            top: 50%;
            left: 50%;
            transform: translate(-50%, -50%);
            z-index: 2;
          }

          .surveil-eye {
            width: ${eyeSizeClasses[variant].split(' ')[0].replace('w-', '')}px;
            height: ${eyeSizeClasses[variant].split(' ')[1].replace('h-', '')}px;
            color: hsl(210 100% 56%);
            stroke-width: 2;
            filter: drop-shadow(0 4px 6px rgba(0,0,0,0.2));
          }

          .surveil-glow {
            position: absolute;
            top: 50%;
            left: 50%;
            width: 80px;
            height: 80px;
            border-radius: 50%;
            background: radial-gradient(circle, hsl(210 100% 56% / 0.4), hsl(210 100% 56% / 0.1));
            transform: translate(-50%, -50%);
            filter: blur(25px);
            z-index: 1;
            animation: surveil-mystical-glow 3s ease-in-out infinite;
          }

          @keyframes surveil-tilt-rotate {
            0% {
              transform: rotate(-15deg) scale(0.8) rotateY(0deg);
            }
            25% {
              transform: rotate(-10deg) scale(0.85) rotateY(90deg);
            }
            50% {
              transform: rotate(-5deg) scale(0.9) rotateY(180deg);
            }
            75% {
              transform: rotate(-10deg) scale(0.85) rotateY(270deg);
            }
            100% {
              transform: rotate(-15deg) scale(0.8) rotateY(360deg);
            }
          }

          @keyframes surveil-mystical-glow {
            0%, 100% {
              transform: translate(-50%, -50%) scale(1);
              opacity: 0.5;
            }
            50% {
              transform: translate(-50%, -50%) scale(1.15);
              opacity: 0.8;
            }
          }
        `}</style>
      </div>
    </div>
  );
}